import { TrackerEvents, trackEvent } from '@/plugins/tracker'
import { MainService } from '@/services/MainService'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import store from '@/store/store'
import { computed, ComputedRef } from 'vue'
import useDialog from '@/composables/useDialog'
import { useI18n } from 'vue-i18n-composable'
import useSpinner from '@/composables/useSpinner'

interface ResponseUseDocsIA {
  addClickHandlerToDocsIAIcon: () => void
  addDocsIATooltipListeners: () => void
  openDocIA: (documentId: string) => Promise<void>
  showDocsIA: ComputedRef<boolean>
}

export default function useDocsIA(): ResponseUseDocsIA {
  const { getDialogError } = useDialog()
  const { t } = useI18n()
  const { showSpinner, hideSpinner } = useSpinner()

  const userHasDocsIA = computed(() => store.getters[`${ModuleNamespaces.AUTH}/userHasDocsIA`])
  const isPortalUser = computed(() => store.getters[`${ModuleNamespaces.AUTH}/getIsPortalUser`])

  const showDocsIA = computed(() => userHasDocsIA.value && !isPortalUser.value)

  const addDocsIATooltipListeners = () => {
    const icons = document.querySelectorAll('.docs-ia-icon')
    icons.forEach((el) => {
      if (el.hasAttribute('data-tooltip-attached')) {
        return
      }
      el.setAttribute('data-tooltip-attached', 'true')
      el.addEventListener('mouseover', (event) => docsIAIconHover(event, el))
      el.addEventListener('mouseout', hideTooltip)
    })
  }

  const addClickHandlerToDocsIAIcon = () => {
    const icons = document.querySelectorAll('.docs-ia-icon')
    icons.forEach((icon: Element) => {
      if (icon.hasAttribute('data-click-attached')) {
        return
      }
      icon.setAttribute('data-click-attached', 'true')
      icon.addEventListener('click', () => {
        const documentId = icon.getAttribute('data-attr')
        if (documentId) {
          openDocIA(documentId)
        }
      })
    })
  }

  const docsIAIconHover = (_event: Event, el: Element) => {
    const tooltip = document.querySelector('.lf-custom-tooltip') as HTMLElement
    const elementCoords = el.getBoundingClientRect()
    if (!tooltip) {
      return
    }
    tooltip.style.left = `${elementCoords.left - 86}px`
    tooltip.style.top = `${elementCoords.top - 46}px`
    tooltip.style.display = 'block'
  }

  const hideTooltip = () => {
    const tooltip = document.querySelector('.lf-custom-tooltip') as HTMLElement
    if (!tooltip) {
      return
    }
    tooltip.style.left = '0px'
    tooltip.style.top = '0px'
    tooltip.style.display = 'none'
  }

  const openDocIA = async (documentId: string) => {
    trackEvent(TrackerEvents.DOCS_IA)
    const url = `v2/document/${documentId}/doc-ia-url`
    try {
      showSpinner(true)
      const { data } = await new MainService().getData(url)
      const { redirectUrl } = data
      window.open(redirectUrl, '_blank')
    } catch (error) {
      getDialogError({
        message: t('components.dialog.file_manager.docs_ia_error').toString(),
        hideSecondaryButton: true
      })
      console.error('Error opening Docs IA:', error)
    } finally {
      hideSpinner()
    }
  }

  return {
    addClickHandlerToDocsIAIcon,
    addDocsIATooltipListeners,
    openDocIA,
    showDocsIA
  }
}
